import { formatDate, formatESTDateTime } from '@dataGrid/gridUtilities';
import appConstants from '@appConstants';
import { ROUTE_CATEGORIES, KPI_LABELS } from './constants';

interface RouteFlags {
  isCreatedByDynamicRouting?: boolean;
  isCreatedManuallyByUser?: boolean;
  isAdjustedManuallyByUser?: boolean;
  isCreatedBySequenceOptimizer?: boolean;
  isCreatedBySystem?: boolean;
}

interface RouteData extends RouteFlags {
  routeId: string;
  routeName: string;
  category?: string;
  isTrapRoute?: boolean;
  lockStatus?: number;
  shipmentDataForRoute?: ShipmentData[];
  [key: string]: any;
}

interface ShipmentData {
  proId: string;
  routeId?: string;
  routeName?: string;
  [key: string]: any;
}

interface FilterParams {
  allShipments: ShipmentData[];
  selectedRoutes: Array<{ routeId: string }>;
}

interface QueryParams {
  planDate?: string;
  terminalCode?: string;
  scenarioId?: string;
  proId?: string;
}

interface KPIItem {
  key: string;
  label: string;
  value: number;
}

interface RowData {
  dynamicOptimizerRunsCount?: number;
  sequenceOptimizerRunsCount?: number;
  optimizationCreatedRoutesCount?: number;
  manuallyAdjustedRoutesCount?: number;
  manuallyCreatedRoutesCount?: number;
  systemCreatedRoutesCount?: number;
  [key: string]: any;
}

export const getRouteCategory = (route: RouteFlags): string => {
  if (route.isCreatedByDynamicRouting) return ROUTE_CATEGORIES.OPTIMIZATION_CREATED;
  if (route.isCreatedManuallyByUser) return ROUTE_CATEGORIES.MANUALLY_CREATED;
  if (route.isAdjustedManuallyByUser) return ROUTE_CATEGORIES.MANUALLY_ADJUSTED;
  if (route.isCreatedBySequenceOptimizer) return ROUTE_CATEGORIES.SEQUENCE_OPTIMIZER;
  if (route.isCreatedBySystem) return ROUTE_CATEGORIES.SYSTEM_CREATED;
  return '';
};

export const getRouteType = (isTrapRoute?: boolean): string => {
  return isTrapRoute ? appConstants.ROUTE_TYPES.TRAP : appConstants.ROUTE_TYPES.CITY;
};

export const formatRouteCategoryForDisplay = (category?: string): string => {
  if (!category) return '';
  return `${category} Route`;
};

export const transformRoutesData = (routes?: RouteData[]): any[] => {
  if (!routes?.length) return [];

  return routes.map((route) => ({
    ...route,
    categoryDisplay: formatRouteCategoryForDisplay(route.category),
    routeType: getRouteType(route.isTrapRoute)
  }));
};

export const flattenShipmentsFromRoutes = (routes?: RouteData[]): ShipmentData[] => {
  if (!routes?.length) return [];

  const shipments: ShipmentData[] = [];
  routes.forEach((route) => {
    if (route.shipmentDataForRoute?.length) {
      route.shipmentDataForRoute.forEach((shipment) => {
        shipments.push({
          ...shipment,
          routeId: route.routeId,
          routeName: route.routeName
        });
      });
    }
  });

  return shipments;
};

export const getFilteredShipments = ({ allShipments, selectedRoutes }: FilterParams): ShipmentData[] => {
  if (!selectedRoutes?.length) {
    return allShipments;
  }
  const selectedRouteIds = new Set(selectedRoutes.map((route) => route.routeId));
  return allShipments.filter((shipment) => selectedRouteIds.has(shipment.routeId!));
};

export const buildRouteProMappingQueryParams = ({ planDate, terminalCode, scenarioId }: QueryParams): string => {
  const params = new URLSearchParams();
  if (planDate) params.append('planDate', planDate);
  if (terminalCode) params.append('terminalCode', terminalCode);
  if (scenarioId) params.append('scenarioId', scenarioId);
  return params.toString();
};

export const buildShipmentHistoryQueryParams = ({ planDate, terminalCode, scenarioId, proId }: QueryParams): string => {
  const params = new URLSearchParams();
  if (planDate) params.append('planDate', planDate);
  if (terminalCode) params.append('terminalCode', terminalCode);
  if (scenarioId) params.append('scenarioId', scenarioId);
  if (proId) params.append('proId', proId);
  return params.toString();
};

export const formatDateForDisplay = (dateString?: string | null): string => {
  return formatDate({ value: dateString });
};

export const formatHourMinuteToEST = (dateTimeString?: string): string => {
  return formatESTDateTime({
    value: dateTimeString,
    dateTimeFormat: appConstants.DATETYPES.TIME.format
  });
};

export const getKPIData = (rowData?: RowData | null): KPIItem[] => {
  if (!rowData) return [];

  return [
    {
      key: 'dynamicOptimizerRuns',
      label: KPI_LABELS.DYNAMIC_OPTIMIZER_RUNS,
      value: rowData.dynamicOptimizerRunsCount ?? 0
    },
    {
      key: 'sequenceOptimizerRuns',
      label: KPI_LABELS.SEQUENCE_OPTIMIZER_RUNS,
      value: rowData.sequenceOptimizerRunsCount ?? 0
    },
    {
      key: 'optimizationCreatedRoutes',
      label: KPI_LABELS.OPTIMIZATION_CREATED_ROUTES,
      value: rowData.optimizationCreatedRoutesCount ?? 0
    },
    {
      key: 'manuallyAdjustedRoutes',
      label: KPI_LABELS.MANUALLY_ADJUSTED_ROUTES,
      value: rowData.manuallyAdjustedRoutesCount ?? 0
    },
    {
      key: 'manuallyCreatedRoutes',
      label: KPI_LABELS.MANUALLY_CREATED_ROUTES,
      value: rowData.manuallyCreatedRoutesCount ?? 0
    },
    {
      key: 'systemCreatedRoutes',
      label: KPI_LABELS.SYSTEM_CREATED_ROUTES,
      value: rowData.systemCreatedRoutesCount ?? 0
    }
  ];
};
